import {
  CircularProgress,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Typography,
} from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { ReattemptType } from "api/resources/assessments/types"
import { AssignmentCategory } from "api/resources/assignments/types"
import { APIError } from "api/utils"
import { useEffect, useState } from "react"
import { handleErrors } from "utils/helpers"

import Note from "./Note"
import Reason from "./Reason"
import ReattemptInfo from "./ReattemptInfo"
import Request from "./Request"

const ReattemptDialog = ({
  open,
  onClose,
  title,
  contentType,
  onSubmit,
  learningContentId,
  userPlannerItemId,
  category,
}: {
  open: boolean
  onClose: () => void
  title: string
  contentType: "assessment" | "assignment"
  onSubmit: () => void
  learningContentId?: string
  userPlannerItemId?: number
  category?: AssignmentCategory | null
}) => {
  const [step, setStep] = useState<
    "reattempt-info" | "request" | "reason" | "note" | undefined
  >()

  const reattemptData = useQuery({
    queryKey: queries.batch.retrieveReattemptData(userPlannerItemId).queryKey,
    queryFn: () =>
      api.batch.retrieveReattemptData({
        urlParams: {
          userPlannerItemId: userPlannerItemId!,
        },
        params: {
          fields: [
            "parent_planner_user_reattempt_counter",
            "parent_planner",
            "user_reattempt_counter",
          ],
        },
      }),
    onError: err => {
      if (err instanceof APIError) {
        handleErrors(err)
      }
    },
    enabled: open && Boolean(userPlannerItemId),
  })

  const { data, isLoading } = useQuery({
    queryFn: () => api.batch.listBatches(),
    queryKey: queries.batch.listBatches().queryKey,
    onError: err => {
      if (err instanceof APIError) {
        handleErrors(err)
      }
    },
    enabled: open,
  })

  useEffect(() => {
    if (reattemptData.data && reattemptData.data.userReattemptCounter) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStep(
        reattemptData.data.userReattemptCounter.reattemptsAllowed === -1
          ? "reattempt-info"
          : "request"
      )
    }
  }, [reattemptData.data])

  const qualificationTitle =
    reattemptData.data?.parentPlanner?.learningItem.shortTitle ||
    reattemptData.data?.parentPlanner?.learningItem.name

  const isAutomatic =
    reattemptData.data?.reattemptType === ReattemptType.AUTOMATIC

  return (
    <Dialog modal open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader hideClose={step === "note"}>
          <DialogTitle
            onBack={
              step === "reason"
                ? () => {
                    if (
                      reattemptData.data &&
                      reattemptData.data.userReattemptCounter
                        .reattemptsAllowed === -1
                    ) {
                      setStep("reattempt-info")
                    } else {
                      setStep("request")
                    }
                  }
                : undefined
            }
          >
            <Typography variant="strong">
              {step === "note"
                ? isAutomatic
                  ? "Your request has been approved"
                  : "Your request has been submitted"
                : "Request Reattempt"}
            </Typography>
          </DialogTitle>
        </DialogHeader>
        {(reattemptData.isLoading || isLoading || !step) && (
          <DialogBody className="py-2.5">
            <div className="flex w-full items-center justify-center">
              <CircularProgress />
            </div>
          </DialogBody>
        )}
        {data && reattemptData.data && (
          <>
            {step === "reattempt-info" && (
              <ReattemptInfo
                reattemptCount={
                  reattemptData.data.parentPlanner &&
                  reattemptData.data.userReattemptCounter.reattemptsAllowed ===
                    -1
                    ? reattemptData.data.parentPlanner.userReattemptCounter
                        .reattemptsAllowed
                    : reattemptData.data.userReattemptCounter.reattemptsAllowed
                }
                title={qualificationTitle}
                onClick={() => {
                  setStep("reason")
                }}
                category={category}
              />
            )}
            {step === "request" && reattemptData.data.userReattemptCounter && (
              <Request
                data={data}
                contentType={contentType}
                title={title}
                userReattemptCounter={reattemptData.data.userReattemptCounter}
                onClick={() => {
                  setStep("reason")
                }}
                qualificationTitle={qualificationTitle}
              />
            )}
            {step === "reason" && reattemptData.data.userReattemptCounter && (
              <Reason
                reattemptType={reattemptData.data.reattemptType}
                onSubmit={() => {
                  setStep("note")
                }}
                contentType={contentType}
                learningContentId={learningContentId}
                userPlannerItemId={userPlannerItemId}
                userReattemptCounter={reattemptData.data.userReattemptCounter}
              />
            )}
            {step === "note" && (
              <Note
                contentType={contentType}
                reattemptType={reattemptData.data.reattemptType}
                onClick={() => {
                  onSubmit()
                  onClose()
                }}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ReattemptDialog
